import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'
import { ScrollTrigger, registerGsap } from '@/lib/gsap'
import { Preloader } from '@/components/generals/preloader'
import { Cursor } from '@/components/generals/cursor'
import { SiteNav } from '@/components/generals/site-nav'
import { MenuOverlay } from '@/components/generals/menu-overlay'
import { SiteFooter } from '@/components/generals/site-footer'
import { PageTransition } from '@/components/generals/page-transition'

export const Layout: FC<LayoutProps> = ({
    children,
    theme = 'dark',
    footer = true,
    className
}): ReactNode => {
    const [menuOpen, setMenuOpen] = useState(false)
    const router = useRouter()

    const toggleMenu = useCallback((): void => {
        setMenuOpen((open) => !open)
    }, [])

    const closeMenu = useCallback((): void => {
        setMenuOpen(false)
    }, [])

    /* Triggers are measured the moment a section mounts, which on a cold load
       is while the preloader still has the page locked at `overflow: hidden`
       and before the webfonts have swapped in. Both of those change every
       element's position, so anything below the fold ends up with start values
       that never resolve and its reveal never fires. Re-measuring after the
       curtain lifts, after the fonts land, and after a route change is what
       keeps that from happening. */
    useEffect(() => {
        registerGsap()

        const refresh = (): void => {
            requestAnimationFrame(() => ScrollTrigger.refresh())
        }

        router.events.on('routeChangeComplete', refresh)
        window.addEventListener('noma:intro-done', refresh)
        window.addEventListener('load', refresh)

        if (document.fonts?.ready) {
            document.fonts.ready.then(refresh).catch(() => undefined)
        }

        const settle = window.setTimeout(refresh, 1200)

        /* The page can also change height long after those moments: a lazy
           image above the fold-line loading in, or a section being added or
           removed. Any of those leaves every trigger below it measured against
           the old layout, so its reveal waits at a position the content no
           longer sits at and the text never comes in. Re-measure whenever the
           page height actually changes, debounced so a burst of image loads
           costs one refresh. */
        let lastHeight = document.body.scrollHeight
        let pending = 0

        const observer = new ResizeObserver(() => {
            const height = document.body.scrollHeight

            if (height === lastHeight) {
                return
            }

            lastHeight = height
            window.clearTimeout(pending)
            pending = window.setTimeout(refresh, 200)
        })

        observer.observe(document.body)

        return () => {
            router.events.off('routeChangeComplete', refresh)
            window.removeEventListener('noma:intro-done', refresh)
            window.removeEventListener('load', refresh)
            window.clearTimeout(settle)
            window.clearTimeout(pending)
            observer.disconnect()
        }
    }, [router.events])

    return (
        <div
            data-theme={theme}
            className={cn(
                'relative min-h-screen',
                theme === 'dark' ? 'bg-ink text-paper' : 'bg-paper text-ink'
            )}
        >
            <Preloader />
            <PageTransition />
            <Cursor />

            <SiteNav onMenuToggle={toggleMenu} menuOpen={menuOpen} theme={theme} />
            <MenuOverlay open={menuOpen} onClose={closeMenu} />

            <div>
                <main id='main' className={cn('relative', className)}>
                    {children}
                </main>

                {footer ? <SiteFooter /> : null}
            </div>
        </div>
    )
}
